import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Close, ExpandMore } from "@mui/icons-material";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import {
  useGetTemplateLibraryQuery,
  useImportLibraryTemplatesMutation,
} from "./templatesApiSlice";
import toast from "../../../helpers/toast";

function TemplateLibraryModal({ open, onClose, targetTemplate, onSuccess }) {
  const { data: library, isLoading, isError, error } =
    useGetTemplateLibraryQuery(undefined, { skip: !open });

  const [importTemplates, { isLoading: importing }] =
    useImportLibraryTemplatesMutation();

  const [selected, setSelected] = useState({});

  const isEditMode = Boolean(targetTemplate);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) setSelected({});
  }, [open]);

  const templateTypes = library ? Object.keys(library).filter((t) => library[t]?.length > 0) : [];

  const totalSelected = Object.values(selected).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  const isTemplateSelected = (type, filename) =>
    (selected[type] || []).includes(filename);

  const toggleTemplate = (type, filename) => {
    if (isEditMode) {
      if (isTemplateSelected(type, filename)) {
        setSelected({});
      } else {
        setSelected({ [type]: [filename] });
      }
      return;
    }

    setSelected((prev) => {
      const current = prev[type] || [];
      if (current.includes(filename)) {
        return { ...prev, [type]: current.filter((f) => f !== filename) };
      }
      return { ...prev, [type]: [...current, filename] };
    });
  };

  const toggleSelectAllType = (type) => {
    if (isEditMode) return;
    const templates = library[type] || [];
    const currentSelected = selected[type] || [];
    if (currentSelected.length === templates.length) {
      setSelected((prev) => ({ ...prev, [type]: [] }));
    } else {
      setSelected((prev) => ({
        ...prev,
        [type]: templates.map((t) => t.filename),
      }));
    }
  };

  const handleImport = async () => {
    const templatesToImport = [];
    for (const type of Object.keys(selected)) {
      for (const filename of selected[type]) {
        const templateInfo = library[type]?.find((t) => t.filename === filename);
        if (templateInfo) {
          templatesToImport.push({
            filename: templateInfo.filename,
            name: templateInfo.name,
            type,
            download_url: templateInfo.download_url,
          });
        }
      }
    }

    if (templatesToImport.length === 0) return;

    try {
      const payload = { templates: templatesToImport };
      if (isEditMode && targetTemplate) {
        payload.overwrite_id = targetTemplate.id;
      }

      const result = await importTemplates(payload).unwrap();
      if (isEditMode && targetTemplate) {
        toast.success(`Successfully overwrote template "${targetTemplate.name}"`);
      } else {
        const count = result?.templates?.length || 0;
        const errorCount = result?.errors?.length || 0;
        if (errorCount > 0) {
          toast.success(`Imported ${count} template(s) with ${errorCount} error(s)`);
        } else {
          toast.success(`Successfully imported ${count} template(s)`);
        }
      }
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(
        e?.data?.message || (isEditMode ? "Failed to overwrite template" : "Failed to import templates")
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {isEditMode ? "Choose Template to Overwrite" : "Template Library"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {isEditMode && targetTemplate && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Overwriting template: <strong>{targetTemplate.name}</strong> ({targetTemplate.type})
          </Alert>
        )}

        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="error">
            {error?.data?.message || "Failed to load template library"}
          </Alert>
        )}

        {!isLoading && !isError && templateTypes.length === 0 && (
          <Alert severity="info">No templates found in the library.</Alert>
        )}

        {templateTypes.map((type) => {
          const templates = library[type];
          const selectedCount = (selected[type] || []).length;
          const allSelected = selectedCount === templates.length;

          return (
            <Accordion key={type} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center" spacing={1} width="100%">
                  <Typography sx={{ textTransform: "capitalize", fontWeight: 600 }}>
                    {type}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${templates.length} template${templates.length !== 1 ? "s" : ""}`}
                  />
                  {selectedCount > 0 && (
                    <Chip size="small" color="primary" label={`${selectedCount} selected`} />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {!isEditMode && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selectedCount > 0 && !allSelected}
                        onChange={() => toggleSelectAllType(type)}
                      />
                    }
                    label={<Typography variant="body2" fontWeight={600}>Select All</Typography>}
                    sx={{ mb: 1 }}
                  />
                )}
                <Box sx={{ display: "flex", flexDirection: "column", ml: isEditMode ? 0 : 3 }}>
                  {templates.map((t) => (
                    <FormControlLabel
                      key={t.filename}
                      control={
                        <Checkbox
                          checked={isTemplateSelected(type, t.filename)}
                          onChange={() => toggleTemplate(type, t.filename)}
                        />
                      }
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{t.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({t.filename})
                          </Typography>
                        </Stack>
                      }
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <StyledLoadingButton
          loading={importing}
          onClick={handleImport}
          disabled={totalSelected === 0}
        >
          {isEditMode
            ? "Overwrite"
            : `Import ${totalSelected > 0 ? `${totalSelected} Template${totalSelected !== 1 ? "s" : ""}` : "Selected"}`}
        </StyledLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default TemplateLibraryModal;
